import { atlasFetch } from './api/atlasBackendClient';
import { sanitizeRecord } from './diagnostics/diagnosticsSanitizer';
import { getSimulationApiBaseUrl } from './getAtlasApiBaseUrl';

const ENABLE_BACKEND_ANALYTICS = process.env.REACT_APP_ENABLE_BACKEND_ANALYTICS === 'true';
const EVENTS_ENDPOINT = `${getSimulationApiBaseUrl()}/analytics/events`;
const SESSION_KEY = 'atlas_backend_analytics_session_id';
const BATCH_SIZE = 50;
const FLUSH_INTERVAL_MS = 12_000;
const MAX_RETRIES = 3;

export interface BackendAnalyticsEvent {
  event_name: string;
  session_id: string;
  object_type?: string;
  object_id?: string;
  meta?: Record<string, unknown>;
}

type QueuedEvent = BackendAnalyticsEvent & {
  retries: number;
};

const queue: QueuedEvent[] = [];
let flushTimer: number | null = null;
let flushing = false;

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback v4 UUID using crypto.getRandomValues
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 1
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function getSessionId(): string {
  if (typeof window === 'undefined') {
    return '00000000-0000-4000-8000-000000000000';
  }
  const existing = window.sessionStorage.getItem(SESSION_KEY);
  if (existing) {
    return existing;
  }
  const created = generateUUID();
  window.sessionStorage.setItem(SESSION_KEY, created);
  return created;
}

async function postEvents(events: BackendAnalyticsEvent[]): Promise<void> {
  const response = await atlasFetch(EVENTS_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ events }),
    keepalive: true,
  });
  if (!response.ok) {
    throw new Error(`Backend analytics ingest failed: HTTP ${response.status}`);
  }
}

function ensureFlushTimer(): void {
  if (typeof window === 'undefined' || flushTimer !== null) {
    return;
  }
  flushTimer = window.setInterval(() => {
    void flushBackendAnalyticsQueue();
  }, FLUSH_INTERVAL_MS);
}

function requeueWithRetry(events: QueuedEvent[]): void {
  events.forEach((entry) => {
    if (entry.retries >= MAX_RETRIES) {
      return;
    }
    queue.unshift({ ...entry, retries: entry.retries + 1 });
  });
}

export function enqueueBackendAnalyticsEvent(
  eventName: string,
  payload: Record<string, unknown>,
  hints?: { objectType?: string; objectId?: string }
): void {
  if (!ENABLE_BACKEND_ANALYTICS) {
    return;
  }
  const event: QueuedEvent = {
    event_name: eventName,
    session_id: getSessionId(),
    object_type: hints?.objectType,
    object_id: hints?.objectId,
    meta: sanitizeRecord(payload),
    retries: 0,
  };
  queue.push(event);
  ensureFlushTimer();
  if (queue.length >= BATCH_SIZE) {
    void flushBackendAnalyticsQueue();
  }
}

export async function flushBackendAnalyticsQueue(): Promise<void> {
  if (!ENABLE_BACKEND_ANALYTICS || flushing || queue.length === 0) {
    return;
  }
  flushing = true;
  let batch: QueuedEvent[] = [];
  try {
    batch = queue.splice(0, BATCH_SIZE);
    const events: BackendAnalyticsEvent[] = batch.map(({ retries: _retries, ...entry }) => entry);
    await postEvents(events);
  } catch {
    // Keep analytics fire-and-forget; failed batches are retried with cap.
    requeueWithRetry(batch);
  } finally {
    flushing = false;
  }
}

export function setupBackendAnalyticsLifecycle(): void {
  if (!ENABLE_BACKEND_ANALYTICS || typeof window === 'undefined') {
    return;
  }
  ensureFlushTimer();
  window.addEventListener('beforeunload', () => {
    void flushBackendAnalyticsQueue();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      void flushBackendAnalyticsQueue();
    }
  });
}

export function resetBackendAnalyticsForTests(): void {
  queue.splice(0, queue.length);
  if (flushTimer !== null && typeof window !== 'undefined') {
    window.clearInterval(flushTimer);
  }
  flushTimer = null;
  flushing = false;
}
